'use client';

import { useEffect, useRef } from 'react';
import { trackEvent } from '@/lib/analytics';

interface ScrollDepthTrackerProps {
  contentSlug: string;
  contentType: string;
}

const MILESTONES = [25, 50, 75, 100] as const;

/**
 * Tracks scroll depth at 25/50/75/100% milestones for all users (anonymous included).
 * Uses IntersectionObserver for performance. Sends events to GA4 via gtag.
 */
export default function ScrollDepthTracker({ contentSlug, contentType }: ScrollDepthTrackerProps) {
  const trackedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const article = document.querySelector('article') || document.querySelector('[role="article"]') || document.getElementById('main-content');
    if (!article) return;

    const sentinels: HTMLDivElement[] = [];

    // Create invisible sentinel elements at each milestone
    for (const milestone of MILESTONES) {
      const sentinel = document.createElement('div');
      sentinel.style.height = '1px';
      sentinel.style.width = '1px';
      sentinel.style.position = 'absolute';
      sentinel.style.left = '0';
      sentinel.style.pointerEvents = 'none';
      sentinel.style.opacity = '0';
      sentinel.dataset.milestone = String(milestone);

      // Position relative to the article's content area
      const articleHeight = article.scrollHeight;
      sentinel.style.top = `${(articleHeight * milestone) / 100}px`;

      article.style.position = article.style.position || 'relative';
      article.appendChild(sentinel);
      sentinels.push(sentinel);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const milestone = Number(entry.target.getAttribute('data-milestone'));
            if (milestone && !trackedRef.current.has(milestone)) {
              trackedRef.current.add(milestone);
              trackEvent('scroll_depth', {
                event_category: 'engagement',
                event_label: `${contentType}/${contentSlug}`,
                value: milestone,
                content_slug: contentSlug,
                content_type: contentType,
                milestone_percent: milestone,
              });
            }
          }
        }
      },
      { threshold: 0 }
    );

    sentinels.forEach((s) => observer.observe(s));

    return () => {
      observer.disconnect();
      sentinels.forEach((s) => s.remove());
    };
  }, [contentSlug, contentType]);

  return null;
}
